// Settings → API → Webhooks: the reference (B6). Everything somebody needs to
// write a receiver without asking us: what we promise about delivery, how to
// check a request is ours, and every event with the payload it carries.
//
// The event list and its samples are the server's catalog, and the numbers
// are the delivery job's own — nothing here is written twice.
import { useMemo, useState } from 'react';
import type { WebhookEventSpec } from '../../api/webhooks';
import { CodeBlock, SectionLabel, TableCard } from '../../components/fs';
import {
  WEBHOOK_HEADERS, WebhookContract, contractLines, exampleEnvelope, nodeSnippet, pythonSnippet,
} from './webhookDocs';

type Lang = 'node' | 'python';

export default function WebhookReference({ catalog, contract }: { catalog: WebhookEventSpec[]; contract: WebhookContract }) {
  const [lang, setLang] = useState<Lang>('node');
  const [open, setOpen] = useState<string | null>(null);

  const groups = useMemo(() => {
    const out: { group: string; events: WebhookEventSpec[] }[] = [];
    for (const e of catalog) {
      const g = out.find((x) => x.group === e.group);
      if (g) g.events.push(e); else out.push({ group: e.group, events: [e] });
    }
    return out;
  }, [catalog]);

  const snippet = lang === 'node' ? nodeSnippet(contract.toleranceSeconds) : pythonSnippet(contract.toleranceSeconds);

  return (
    <TableCard
      className="wh-ref"
      title="Webhook reference"
      hint={<>What an endpoint is sent, how to check it came from us, and what we promise about delivery. Payload version <code>{contract.apiVersion}</code>.</>}
    >
      <div className="wh-ref-body">
        <section aria-label="Delivery">
          <SectionLabel as="h3">Delivery</SectionLabel>
          <dl className="wh-contract">
            {contractLines(contract).map((l) => (
              <div key={l.title}><dt>{l.title}</dt><dd>{l.text}</dd></div>
            ))}
          </dl>
        </section>

        <section aria-label="Checking a request is ours">
          <SectionLabel as="h3">Checking a request is ours</SectionLabel>
          <dl className="wh-headers">
            {WEBHOOK_HEADERS.map((h) => (
              <div key={h.name}><dt><code>{h.name}</code></dt><dd>{h.text}</dd></div>
            ))}
          </dl>
          <div className="fs-seg wh-lang" role="group" aria-label="Example language">
            <button type="button" className={lang === 'node' ? 'on' : ''} aria-pressed={lang === 'node'} onClick={() => setLang('node')}>Node.js</button>
            <button type="button" className={lang === 'python' ? 'on' : ''} aria-pressed={lang === 'python'} onClick={() => setLang('python')}>Python</button>
          </div>
          <CodeBlock code={snippet} label="Signature check example" />
        </section>

        {groups.length > 0 && (
          <section aria-label="Events">
            <SectionLabel as="h3">Events</SectionLabel>
            <p className="wh-ref-note">
              Every event arrives in the same envelope — <code>id</code>, <code>type</code>, <code>apiVersion</code>, <code>createdAt</code>,{' '}
              <code>account</code> — with what happened under <code>data</code>. Open one to see an example.
            </p>
            {groups.map((g) => (
              <div className="wh-ref-group" key={g.group}>
                <div className="wh-ref-group-name">{g.group}</div>
                <ul className="wh-events">
                  {g.events.map((e) => {
                    const isOpen = open === e.type;
                    return (
                      <li key={e.type}>
                        <button type="button" className="wh-event" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : e.type)}>
                          <span className="what"><b>{e.label}</b><small>{e.description}</small></span>
                          <code>{e.type}</code>
                        </button>
                        {isOpen && (
                          <div className="wh-event-sample">
                            <CodeBlock code={JSON.stringify(exampleEnvelope(e, contract.apiVersion), null, 2)} label={`Example ${e.type} payload`} />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </section>
        )}
      </div>
    </TableCard>
  );
}
